import getConfig from "../config";

export type GlobalImportContext = "ecm-certificates" | "ecm-customers" | "ecm-vendors";

/**
 * Shell base URL: One Web Domain uses `AvaAppConfig.hostnames.avaApp`; standalone uses
 * `VITE_AVA_APP_HOST` from runtime config or `.env`. See `getConfig().hostnames.avaApp`.
 */
const resolveImportsBase = (): string => {
    const raw = getConfig().hostnames.avaApp?.trim() ?? "";
    if (!raw) {
        throw new Error(
            "Global Imports requires hostnames.avaApp (AvaAppConfig.hostnames.avaApp)."
        );
    }
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return raw.replace(/\/$/, "");
    }
    return `https://${raw.replace(/\/$/, "")}`;
};

export function getGlobalImportsUrl(context: GlobalImportContext): string {
    const url = new URL("/imports", `${resolveImportsBase()}/`);
    url.searchParams.set("context", context);
    return url.toString();
}

export function assignToGlobalImports(context: GlobalImportContext): void {
    window.location.assign(getGlobalImportsUrl(context));
}

/**
 * Use AvaApp Global Imports only when enabled for the environment, the user is
 * authorized, and an AvaApp host is configured.
 *
 * Enablement is env-driven via `getConfig().enableGlobalImports`. Standalone resolves it
 * from `VITE_ENABLE_GLOBAL_IMPORTS` (runtime `/config.json` or `.env`); One Web Domain
 * (AvaApp) resolves it per-environment from `ecmConfigForAvaApp` in `config.ts`.
 */
export function shouldUseGlobalImportShell(isAuthorizedGlobalImports: boolean): boolean {
    if (!getConfig().enableGlobalImports) {
        return false;
    }
    if (!isAuthorizedGlobalImports) {
        return false;
    }
    const raw = getConfig().hostnames.avaApp?.trim() ?? "";
    return Boolean(raw);
}
